import { takeEvery, fork, delay, call, put, select } from 'redux-saga/effects';
import { ActionTypes, AiTextGenerateStartAction, AiTextGenerateWaitAction, AiTextItemType } from '@/store/aiText/types';
import {
    generateAiText as generateAiTextApi,
    getGeneratedAiText as getGeneratedAiTextApi,
} from '@/services/ApiCategories/aiText';
import { UpdateAiToolsTextCount, getAccountLimits, getAccountOverview, getGeneralInfo } from '@/store/account/actions';
import { PromptType } from '@dev/ee-api/ee-api-models';
import { AiTextGenerateEnd, AiTextGenerateStart, AiTextGenerateWait } from '../store/aiText/actions';
import { errorToast } from './commonGenerators/errorToast';
const AI_TEXT_COUNT_RESPONSE_HEADER = 'x-aitools-text-count';

const getSystem = (type: AiTextItemType) => {
    if (type === AiTextItemType.GENERATE) {
        return PromptType.GenerateText;
    }
    if (type === AiTextItemType.IMPROVE) {
        return PromptType.ImproveText;
    }
    return PromptType.GenerateText;
};

function* generateStartCheck(action: AiTextGenerateStartAction): any {
    const { IsExpired } = yield select(getGeneralInfo);
    const { AIToolsTextCount } = yield select(getAccountOverview);
    const { AiToolsTextLimit } = yield select(getAccountLimits);

    if (IsExpired || AiToolsTextLimit - AIToolsTextCount < 1) {
        return;
    }

    yield put(AiTextGenerateStart(action.payload.item));
}

function* generateStart(action: AiTextGenerateStartAction): any {
    try {
        const { AIToolsTextCount } = yield select(getAccountOverview);
        const { AiToolsTextLimit } = yield select(getAccountLimits);

        if (AiToolsTextLimit - AIToolsTextCount < 0) {
            return;
        }

        const system = getSystem(action.payload.item.type);
        const response = yield call(generateAiTextApi, action.payload.item.request, system);
        const conversationId = response.data;
        const numberOfTries = 30;
        yield put(AiTextGenerateWait(action.payload.item, conversationId, numberOfTries));
    } catch (e) {
        yield put(
            AiTextGenerateEnd({
                ...action.payload.item,
                isTimeout: true,
            })
        );
        yield call(errorToast, e);
    }
}

function* generateWait(action: AiTextGenerateWaitAction): any {
    try {
        const { conversationId, numberOfTries = 30 } = action.payload;
        let numberOfTry = 1;

        yield delay(3000);
        let generatedResponse = yield call(getGeneratedAiTextApi, conversationId);

        if (!generatedResponse.data?.length) {
            while (numberOfTry < numberOfTries) {
                const delayTime = numberOfTry <= 10 ? 3000 : 5000;
                yield delay(delayTime);
                generatedResponse = yield call(getGeneratedAiTextApi, conversationId);
                if (!generatedResponse.data?.length) {
                    numberOfTry++;
                } else {
                    break;
                }
            }
        }

        yield put(
            AiTextGenerateEnd({
                ...action.payload.item,
                isTimeout: !generatedResponse.data?.length,
                response: generatedResponse.data?.length ? generatedResponse.data?.[0]?.Response : null,
            })
        );
        const usedCount = generatedResponse?.headers[AI_TEXT_COUNT_RESPONSE_HEADER]
            ? parseInt(generatedResponse?.headers[AI_TEXT_COUNT_RESPONSE_HEADER], 10)
            : -1;
        if (usedCount > -1) {
            yield put(UpdateAiToolsTextCount(usedCount));
        }
    } catch (e) {
        yield put(
            AiTextGenerateEnd({
                ...action.payload.item,
                isTimeout: true,
            })
        );
        yield call(errorToast, e);
    }
}

function* generateStartCheckWatcher(): any {
    yield takeEvery(ActionTypes.GENERATE_START_CHECK, generateStartCheck);
}

function* generateStartWatcher(): any {
    yield takeEvery(ActionTypes.GENERATE_START, generateStart);
}

function* generateWaitWatcher(): any {
    yield takeEvery(ActionTypes.GENERATE_WAIT, generateWait);
}

const aiTextSagas = [fork(generateStartCheckWatcher), fork(generateStartWatcher), fork(generateWaitWatcher)];

export default aiTextSagas;
